import React, { useState, useEffect } from 'react'
import { XMarkIcon, ChevronLeftIcon, ChevronRightIcon, PlusIcon, TrashIcon, PencilIcon } from '@heroicons/react/24/outline'
import { CheckCircleIcon } from '@heroicons/react/24/solid'
import { supabase } from '../../lib/supabase-auth'
import { useAuth } from '../../contexts/AuthContextLazy'
import { projectTemplates, ProjectTemplate, calculateMilestoneDates } from '../../utils/project-templates'
import { VisualJourneyCanvas } from '../project/VisualJourneyCanvas'
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core'
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

interface ProjectSetupWizardProps {
  isOpen: boolean
  onClose: () => void
  initialClientId?: string
  onProjectCreated?: (projectId: string) => void
}

interface ProjectTask {
  id: string
  name: string
  description: string
  task_type: 'task' | 'milestone' | 'compliance_task'
  due_date: string | null
  depends_on: string[]
  document_requirements?: string[]
  order_index: number
}

interface FormData {
  name: string
  description: string
  client_id: string
  start_date: string
  end_date: string
  status: 'active' | 'on_hold' | 'completed' | 'cancelled'
  use_template: boolean
  template_id: string
  tasks: ProjectTask[]
}

interface Party {
  id: string
  name: string
  type: string
  status: string
}

// Sortable Task Item Component
const SortableTaskItem: React.FC<{
  task: ProjectTask
  onEdit: () => void
  onDelete: () => void
  allTasks: ProjectTask[]
}> = ({ task, onEdit, onDelete, allTasks }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  const getTaskTypeColor = (type: string) => {
    switch (type) {
      case 'milestone':
        return 'bg-purple-100 text-purple-800'
      case 'compliance_task':
        return 'bg-amber-100 text-amber-800'
      default:
        return 'bg-blue-100 text-blue-800'
    }
  }

  const getDependencyNames = (depends_on: string[]) => {
    return depends_on
      .map(id => allTasks.find(t => t.id === id)?.name)
      .filter(Boolean)
      .join(', ')
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="bg-white portal-content-shadow p-4 rounded-lg border border-gray-200 shadow-sm"
    >
      <div className="flex items-start justify-between">
        <div className="flex items-start flex-1">
          <div
            {...attributes}
            {...listeners}
            className="mr-3 mt-1 cursor-move text-gray-400 hover:text-gray-600"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
            </svg>
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h4 className="font-medium text-gray-900">{task.name}</h4>
              <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getTaskTypeColor(task.task_type)}`}>
                {task.task_type.replace('_', ' ')}
              </span>
            </div>
            {task.description && (
              <p className="text-sm text-gray-600 mb-2">{task.description}</p>
            )}
            <div className="flex flex-wrap gap-4 text-xs text-gray-500">
              {task.due_date && (
                <span>Due: {new Date(task.due_date).toLocaleDateString()}</span>
              )}
              {task.depends_on.length > 0 && (
                <span>Depends on: {getDependencyNames(task.depends_on)}</span>
              )}
              {task.document_requirements && task.document_requirements.length > 0 && (
                <span>{task.document_requirements.length} document(s) required</span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1 ml-4">
          <button
            onClick={onEdit}
            className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
          >
            <PencilIcon className="h-4 w-4" />
          </button>
          <button
            onClick={onDelete}
            className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  )
}

// Task Edit Modal Component
const TaskEditModal: React.FC<{
  isOpen: boolean
  onClose: () => void
  onSave: (task: ProjectTask) => void
  task?: ProjectTask
  allTasks: ProjectTask[]
}> = ({ isOpen, onClose, onSave, task, allTasks }) => {
  const [formData, setFormData] = useState<ProjectTask>({
    id: '',
    name: '',
    description: '',
    task_type: 'task',
    due_date: null,
    depends_on: [],
    document_requirements: [],
    order_index: 0,
  })

  useEffect(() => {
    if (task) {
      setFormData(task)
    } else {
      setFormData({
        id: `task-${Date.now()}`,
        name: '',
        description: '',
        task_type: 'task',
        due_date: null,
        depends_on: [],
        document_requirements: [],
        order_index: allTasks.length,
      })
    }
  }, [task, allTasks.length])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSave(formData)
    onClose()
  }

  const addDocumentRequirement = () => {
    setFormData({
      ...formData,
      document_requirements: [...(formData.document_requirements || []), '']
    })
  }

  const updateDocumentRequirement = (index: number, value: string) => {
    const updated = [...(formData.document_requirements || [])]
    updated[index] = value
    setFormData({ ...formData, document_requirements: updated })
  }

  const removeDocumentRequirement = (index: number) => {
    const updated = formData.document_requirements?.filter((_, i) => i !== index) || []
    setFormData({ ...formData, document_requirements: updated })
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="fixed inset-0 transition-opacity" onClick={onClose}>
          <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        </div>
        <div className="relative bg-white portal-content-shadow rounded-lg max-w-2xl w-full p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {task ? 'Edit Task' : 'Add New Task'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Task Name</label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="mt-1 block w-full bg-white text-gray-900 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Description</label>
              <textarea
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="mt-1 block w-full bg-white text-gray-900 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Task Type</label>
              <select
                value={formData.task_type}
                onChange={(e) => setFormData({ ...formData, task_type: e.target.value as any })}
                className="mt-1 block w-full bg-white text-gray-900 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              >
                <option value="task">Task</option>
                <option value="milestone">Milestone</option>
                <option value="compliance_task">Compliance Task</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Due Date</label>
              <input
                type="date"
                value={formData.due_date || ''}
                onChange={(e) => setFormData({ ...formData, due_date: e.target.value || null })}
                className="mt-1 block w-full bg-white text-gray-900 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Dependencies</label>
              <select
                multiple
                value={formData.depends_on}
                onChange={(e) => {
                  const selected = Array.from(e.target.selectedOptions, option => option.value)
                  setFormData({ ...formData, depends_on: selected })
                }}
                className="mt-1 block w-full bg-white text-gray-900 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                size={4}
              >
                {allTasks
                  .filter(t => t.id !== formData.id)
                  .map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))
                }
              </select>
              <p className="mt-1 text-xs text-gray-500">Hold Ctrl/Cmd to select multiple</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Document Requirements
              </label>
              {formData.document_requirements?.map((doc, index) => (
                <div key={index} className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={doc}
                    onChange={(e) => updateDocumentRequirement(index, e.target.value)}
                    placeholder="Document name"
                    className="flex-1 bg-white text-gray-900 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => removeDocumentRequirement(index)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={addDocumentRequirement}
                className="mt-2 inline-flex items-center px-3 py-1 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <PlusIcon className="h-4 w-4 mr-1" />
                Add Document
              </button>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700"
              >
                {task ? 'Save Changes' : 'Add Task'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export const ProjectSetupWizard: React.FC<ProjectSetupWizardProps> = ({
  isOpen,
  onClose,
  initialClientId,
  onProjectCreated,
}) => {
  const { organization, userProfile } = useAuth()
  const [currentStep, setCurrentStep] = useState(1)
  const [clients, setClients] = useState<Party[]>([])
  const [loadingClients, setLoadingClients] = useState(false)
  const [showTaskModal, setShowTaskModal] = useState(false)
  const [editingTask, setEditingTask] = useState<ProjectTask | undefined>()
  const [showPreview, setShowPreview] = useState(false)
  const [creating, setCreating] = useState(false)

  const [formData, setFormData] = useState<FormData>({
    name: '',
    description: '',
    client_id: initialClientId || '',
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    status: 'active',
    use_template: false,
    template_id: '',
    tasks: [],
  })

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  useEffect(() => {
    if (isOpen) {
      fetchClients()
    }
  }, [isOpen])

  const fetchClients = async () => {
    setLoadingClients(true)
    try {
      const { data, error } = await supabase
        .from('parties')
        .select('id, name, type, status')
        .eq('status', 'active')
        .order('name')

      if (error) throw error
      setClients(data || [])
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error fetching clients:', error)
      }
    } finally {
      setLoadingClients(false)
    }
  }

  const handleTemplateSelect = (templateId: string) => {
    const template = projectTemplates.find(t => t.id === templateId)
    if (template) {
      const milestoneDates = calculateMilestoneDates(template, new Date(formData.start_date))
      const tasks: ProjectTask[] = template.milestones.map((milestone, index) => ({
        id: `milestone-${index}`,
        name: milestone.name,
        description: milestone.description,
        task_type: 'milestone',
        due_date: milestoneDates[index].dueDate.toISOString().split('T')[0],
        depends_on: index > 0 ? [`milestone-${index - 1}`] : [],
        document_requirements: template.required_documents
          .filter(doc => doc.is_required)
          .map(doc => doc.name),
        order_index: index,
      }))

      setFormData({
        ...formData,
        template_id: templateId,
        name: template.name,
        description: template.description,
        tasks,
        end_date: new Date(
          new Date(formData.start_date).getTime() + template.default_duration_days * 24 * 60 * 60 * 1000
        ).toISOString().split('T')[0],
      })
    }
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (active.id !== over?.id) {
      setFormData(prev => {
        const oldIndex = prev.tasks.findIndex(t => t.id === active.id)
        const newIndex = prev.tasks.findIndex(t => t.id === over?.id)
        const newTasks = arrayMove(prev.tasks, oldIndex, newIndex)
        
        // Update order_index for all tasks
        return {
          ...prev,
          tasks: newTasks.map((task, index) => ({ ...task, order_index: index }))
        }
      })
    }
  }

  const handleTaskSave = (task: ProjectTask) => {
    setFormData(prev => {
      const existingIndex = prev.tasks.findIndex(t => t.id === task.id)
      if (existingIndex >= 0) {
        const updated = [...prev.tasks]
        updated[existingIndex] = task
        return { ...prev, tasks: updated }
      } else {
        return { ...prev, tasks: [...prev.tasks, task] }
      }
    })
  }

  const handleTaskDelete = (taskId: string) => {
    setFormData(prev => ({
      ...prev,
      tasks: prev.tasks.filter(t => t.id !== taskId)
    }))
  }

  const validateStep = (step: number): boolean => {
    switch (step) {
      case 1:
        return !!(formData.name && formData.client_id && formData.start_date)
      case 2:
        return formData.use_template ? !!formData.template_id : true
      case 3:
        return formData.tasks.length > 0
      case 4:
        return true
      default:
        return false
    }
  }

  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep === 2 && !formData.use_template) {
        // Skip to task builder if building custom
        setCurrentStep(3)
      } else {
        setCurrentStep(currentStep + 1)
      }
    }
  }

  const handleBack = () => {
    if (currentStep === 3 && !formData.use_template) {
      // Skip template selection if building custom
      setCurrentStep(1)
    } else {
      setCurrentStep(currentStep - 1)
    }
  }

  const handleCreate = async () => {
    setCreating(true)
    try {
      // Create the project
      const { data: project, error: projectError } = await supabase
        .from('projects')
        .insert({
          organization_id: organization!.id,
          name: formData.name,
          description: formData.description,
          client_id: formData.client_id,
          status: formData.status,
          start_date: formData.start_date,
          end_date: formData.end_date || null,
        })
        .select()
        .single()

      if (projectError) throw projectError

      // Create tasks
      const tasksToInsert = formData.tasks.map(task => ({
        project_id: project.id,
        name: task.name,
        description: task.description,
        task_type: task.task_type,
        status: 'pending',
        due_date: task.due_date,
        order_index: task.order_index,
        metadata: {
          document_requirements: task.document_requirements,
        },
        assigned_to: userProfile?.id || null,
        depends_on: task.depends_on.length > 0 ? task.depends_on : null,
      }))

      const { error: tasksError } = await supabase
        .from('project_tasks')
        .insert(tasksToInsert)

      if (tasksError) throw tasksError

      if (onProjectCreated) {
        onProjectCreated(project.id)
      }

      onClose()
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error creating project:', error)
      }
      alert('Failed to create project. Please try again.')
    } finally {
      setCreating(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="fixed inset-0 transition-opacity" onClick={onClose}>
          <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        </div>
        
        <div className="relative bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">Project Setup Wizard</h2>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            {/* Progress Indicator */}
            <div className="mt-4">
              <div className="flex items-center justify-between">
                {[1, 2, 3, 4].map((step) => (
                  <div key={step} className="flex items-center">
                    <div
                      className={`flex items-center justify-center w-8 h-8 rounded-full ${
                        step < currentStep
                          ? 'bg-blue-600 text-white'
                          : step === currentStep
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 text-gray-600'
                      }`}
                    >
                      {step < currentStep ? (
                        <CheckCircleIcon className="h-5 w-5" />
                      ) : (
                        step
                      )}
                    </div>
                    {step < 4 && (
                      <div
                        className={`w-full h-1 mx-2 ${
                          step < currentStep ? 'bg-blue-600' : 'bg-gray-200'
                        }`}
                        style={{ width: '120px' }}
                      />
                    )}
                  </div>
                ))}
              </div>
              <div className="flex justify-between mt-2">
                <span className="text-xs text-gray-600">Project Basics</span>
                <span className="text-xs text-gray-600">Template</span>
                <span className="text-xs text-gray-600">Tasks</span>
                <span className="text-xs text-gray-600">Review</span>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {/* Step 1: Project Basics */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <h3 className="text-lg font-medium text-gray-900">Project Basics</h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Client <span className="text-red-500">*</span>
                  </label>
                  <select
                    required
                    value={formData.client_id}
                    onChange={(e) => setFormData({ ...formData, client_id: e.target.value })}
                    className="mt-1 block w-full bg-white text-gray-900 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    disabled={loadingClients}
                  >
                    <option value="">Select a client...</option>
                    {clients.map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.name} ({client.type})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Project Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="mt-1 block w-full bg-white text-gray-900 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <textarea
                    rows={3}
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="mt-1 block w-full bg-white text-gray-900 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Start Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      required
                      value={formData.start_date}
                      onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                      className="mt-1 block w-full bg-white text-gray-900 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">End Date</label>
                    <input
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                      className="mt-1 block w-full bg-white text-gray-900 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                    className="mt-1 block w-full bg-white text-gray-900 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    <option value="active">Active</option>
                    <option value="on_hold">On Hold</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
              </div>
            )}

            {/* Step 2: Template Selection */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <h3 className="text-lg font-medium text-gray-900">Choose Your Approach</h3>
                
                <div className="space-y-4">
                  <label className="flex items-center p-4 border rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      type="radio"
                      name="approach"
                      checked={formData.use_template}
                      onChange={() => setFormData({ ...formData, use_template: true })}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 bg-white"
                    />
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-900">Use a Template</p>
                      <p className="text-sm text-gray-500">Start with a pre-built workflow for common project types</p>
                    </div>
                  </label>

                  <label className="flex items-center p-4 border rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      type="radio"
                      name="approach"
                      checked={!formData.use_template}
                      onChange={() => setFormData({ ...formData, use_template: false, template_id: '', tasks: [] })}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 bg-white"
                    />
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-900">Build Custom Workflow</p>
                      <p className="text-sm text-gray-500">Create a unique task workflow from scratch</p>
                    </div>
                  </label>
                </div>

                {formData.use_template && (
                  <div className="mt-6">
                    <h4 className="text-sm font-medium text-gray-900 mb-4">Select a Template</h4>
                    <div className="grid grid-cols-2 gap-4">
                      {projectTemplates.map((template) => (
                        <button
                          key={template.id}
                          onClick={() => handleTemplateSelect(template.id)}
                          className={`p-4 border rounded-lg text-left hover:border-blue-500 transition-colors ${
                            formData.template_id === template.id
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200'
                          }`}
                        >
                          <div className="flex items-start">
                            <span className="text-2xl mr-3">{template.icon}</span>
                            <div className="flex-1">
                              <h5 className="font-medium text-gray-900">{template.name}</h5>
                              <p className="text-sm text-gray-500 mt-1">{template.description}</p>
                              <div className="mt-2 flex gap-4 text-xs text-gray-400">
                                <span>{template.milestones.length} milestones</span>
                                <span>{template.default_duration_days} days</span>
                              </div>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Step 3: Task Builder */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-gray-900">Task Workflow</h3>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowPreview(!showPreview)}
                      className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                      {showPreview ? 'Hide Preview' : 'Show Visual Preview'}
                    </button>
                    <button
                      onClick={() => {
                        setEditingTask(undefined)
                        setShowTaskModal(true)
                      }}
                      className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 flex items-center"
                    >
                      <PlusIcon className="h-4 w-4 mr-1" />
                      Add Task
                    </button>
                  </div>
                </div>

                {showPreview && formData.tasks.length > 0 && (
                  <div className="border rounded-lg p-4 bg-gray-50" style={{ height: '400px' }}>
                    <VisualJourneyCanvas
                      projectId="preview"
                      tasks={formData.tasks.map(t => ({
                        ...t,
                        status: 'pending' as const,
                        assigned_to: null,
                        metadata: { document_requirements: t.document_requirements },
                        blocks: null,
                        compliance_document_id: null,
                      }))}
                      selectedTaskId={null}
                      onTaskSelect={() => {}}
                      onTaskStatusChange={() => {}}
                      viewMode="vertical"
                    />
                  </div>
                )}

                <div className="space-y-3">
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={formData.tasks.map(t => t.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      {formData.tasks.map((task) => (
                        <SortableTaskItem
                          key={task.id}
                          task={task}
                          onEdit={() => {
                            setEditingTask(task)
                            setShowTaskModal(true)
                          }}
                          onDelete={() => handleTaskDelete(task.id)}
                          allTasks={formData.tasks}
                        />
                      ))}
                    </SortableContext>
                  </DndContext>

                  {formData.tasks.length === 0 && (
                    <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                      <p className="text-gray-500 mb-4">No tasks added yet</p>
                      <button
                        onClick={() => {
                          setEditingTask(undefined)
                          setShowTaskModal(true)
                        }}
                        className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700"
                      >
                        Add your first task
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Step 4: Review */}
            {currentStep === 4 && (
              <div className="space-y-6">
                <h3 className="text-lg font-medium text-gray-900">Review & Create</h3>
                
                <div className="bg-gray-50 rounded-lg p-6 space-y-4">
                  <div>
                    <h4 className="text-sm font-medium text-gray-700">Project Details</h4>
                    <dl className="mt-2 space-y-2">
                      <div className="flex justify-between">
                        <dt className="text-sm text-gray-500">Name:</dt>
                        <dd className="text-sm text-gray-900">{formData.name}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-sm text-gray-500">Client:</dt>
                        <dd className="text-sm text-gray-900">
                          {clients.find(c => c.id === formData.client_id)?.name}
                        </dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-sm text-gray-500">Duration:</dt>
                        <dd className="text-sm text-gray-900">
                          {formData.start_date} to {formData.end_date || 'Ongoing'}
                        </dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-sm text-gray-500">Status:</dt>
                        <dd className="text-sm text-gray-900">{formData.status}</dd>
                      </div>
                    </dl>
                  </div>

                  <div className="border-t pt-4">
                    <h4 className="text-sm font-medium text-gray-700">Workflow Summary</h4>
                    <dl className="mt-2 space-y-2">
                      <div className="flex justify-between">
                        <dt className="text-sm text-gray-500">Total Tasks:</dt>
                        <dd className="text-sm text-gray-900">{formData.tasks.length}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-sm text-gray-500">Milestones:</dt>
                        <dd className="text-sm text-gray-900">
                          {formData.tasks.filter(t => t.task_type === 'milestone').length}
                        </dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-sm text-gray-500">Compliance Tasks:</dt>
                        <dd className="text-sm text-gray-900">
                          {formData.tasks.filter(t => t.task_type === 'compliance_task').length}
                        </dd>
                      </div>
                    </dl>
                  </div>

                  {formData.description && (
                    <div className="border-t pt-4">
                      <h4 className="text-sm font-medium text-gray-700">Description</h4>
                      <p className="mt-1 text-sm text-gray-900">{formData.description}</p>
                    </div>
                  )}
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-sm text-amber-800">
                    Once created, you can further customize the project by adding team members,
                    adjusting timelines, and tracking progress.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 flex justify-between">
            <button
              onClick={handleBack}
              disabled={currentStep === 1}
              className={`px-4 py-2 text-sm font-medium rounded-md ${
                currentStep === 1
                  ? 'text-gray-400 bg-gray-100 cursor-not-allowed'
                  : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
              }`}
            >
              <ChevronLeftIcon className="h-4 w-4 inline mr-1" />
              Back
            </button>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  // TODO: Implement save draft functionality
                  alert('Draft saving not yet implemented')
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Save Draft
              </button>

              {currentStep < 4 ? (
                <button
                  onClick={handleNext}
                  disabled={!validateStep(currentStep)}
                  className={`px-4 py-2 text-sm font-medium rounded-md ${
                    validateStep(currentStep)
                      ? 'text-white bg-blue-600 hover:bg-blue-700'
                      : 'text-gray-400 bg-gray-100 cursor-not-allowed'
                  }`}
                >
                  Next
                  <ChevronRightIcon className="h-4 w-4 inline ml-1" />
                </button>
              ) : (
                <button
                  onClick={handleCreate}
                  disabled={creating || !validateStep(4)}
                  className={`px-4 py-2 text-sm font-medium rounded-md ${
                    creating || !validateStep(4)
                      ? 'text-gray-400 bg-gray-100 cursor-not-allowed'
                      : 'text-white bg-blue-600 hover:bg-blue-700'
                  }`}
                >
                  {creating ? 'Creating...' : 'Create Project'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Task Edit Modal */}
      <TaskEditModal
        isOpen={showTaskModal}
        onClose={() => {
          setShowTaskModal(false)
          setEditingTask(undefined)
        }}
        onSave={handleTaskSave}
        task={editingTask}
        allTasks={formData.tasks}
      />
    </div>
  )
}